// Each person's otter: a "look" (body, fur, belly, scarf, accessories), made from their Raft id until they
// dress it up themselves. Sprites are recoloured from a template sheet per body, and the accessories drawn on top.
import { GEO3D, FILES3D } from '/otter3d/geo.js';
export { GEO3D };
import { ACCESSORIES, accById, accOf, okAcc, okTint, BODY_KEYS, PART_COUNTS, seedParts } from '/look-model.js';
export { ACCESSORIES, SLOTS, clash, wear } from '/look-model.js';

// names: catalog keys (public/locales), worded by the dress-up panel (dress.js)
export const FURS = [
  { name: 'dress.fur.brown', fur: [138, 90, 59], shade: [107, 67, 41] },
  { name: 'dress.fur.darkBrown', fur: [104, 66, 44], shade: [78, 48, 32] },
  { name: 'dress.fur.caramel', fur: [188, 128, 70], shade: [150, 98, 48] },
  { name: 'dress.fur.grey', fur: [120, 122, 132], shade: [88, 90, 100] },
  { name: 'dress.fur.cream', fur: [214, 182, 140], shade: [178, 146, 106] },
  { name: 'dress.fur.black', fur: [62, 56, 58], shade: [40, 36, 38] },
];
export const BELLIES = [
  { name: 'dress.belly.cream', c: [241, 220, 184] }, { name: 'dress.belly.white', c: [250, 248, 242] }, { name: 'dress.belly.lightGold', c: [246, 214, 150] },
];
export const SCARVES = [
  { name: 'dress.scarf.coral', c: [255, 107, 74] }, { name: 'dress.scarf.raftYellow', c: [253, 208, 59] }, { name: 'dress.scarf.sky', c: [80, 146, 250] },
  { name: 'dress.scarf.mint', c: [70, 200, 140] }, { name: 'dress.scarf.purple', c: [168, 85, 247] }, { name: 'dress.scarf.pink', c: [244, 114, 182] },
  { name: 'dress.scarf.navy', c: [30, 58, 138] }, { name: 'dress.none', c: null },
];
/** Two-tone colour presets for the dress-up panel: palette picks, and a tint where no palette entry fits. */
export const PRESETS = [
  { name: 'dress.preset.classic', fur: 0, belly: 0, scarf: 0 },
  { name: 'dress.preset.snow', fur: 4, belly: 1, scarf: 2 },
  { name: 'dress.preset.night', fur: 5, belly: 2, scarf: 1 },
  { name: 'dress.preset.mintGrey', fur: 3, belly: 1, scarf: 3 },
  { name: 'dress.preset.caramel', fur: 2, belly: 0, scarf: 6 },
  { name: 'dress.preset.sakura', fur: 4, belly: 1, scarf: 5, tint: { fur: '#d09888' } },
  { name: 'dress.preset.seaSalt', fur: 3, belly: 1, scarf: 6, tint: { fur: '#7890a8' } },
];
// ---- the otters: the 3D ones (assets/otters3d, 16 directions), the only kind since 「经典水獭」 (the hand-drawn,
// 8-direction sheets of assets/otters/otters.py) was retired. OTTER3D stays exported (always true) for the modules that
// still read it (deck/pose.js, otterbonk/client.js); their classic branches are dead code now. Everything else in the
// app asks this module for frames (ANIM, facingFrame, runFrames, frames16 ...) and sheets (buildSheet), so it follows.
export const OTTER3D = true;
const KEY3D = BODY_KEYS;
/** 体型: 0 is the original otter. The sheets come from
 *  assets/otters3d/render.mjs (public/otter3d/geo.js); `sheet` is the template to load. */
const BODY_INFO = {
  0: { name: 'dress.body.original' },
  A: { name: 'dress.body.roundClassic' },
  B: { name: 'dress.body.seaOtter' },
  C: { name: 'dress.body.bigEyes' },
  Y: { name: 'dress.body.yellow' }, // Raft's mascot
  K: { name: 'dress.body.cat' },     // a cat
  G: { name: 'dress.body.greyCat' },
  M: { name: 'dress.body.salaryCat', colours: { fur: 4, belly: 1 } }, // the sticker's: 奶油 + 白
};
/** A look switched to body b gets that body's own colours, if it has any (月薪喵: 毛色 奶油 + 肚皮 白; their tints
 *  cleared). Only on switching: they can be changed afterwards, nothing is locked. Changes l in place. */
export function bodyColours(l, b) {
  const c = BODIES[b]?.colours; if (!c) return l;
  Object.assign(l, c);
  if (l.tint) { const t = { ...l.tint }; for (const k of Object.keys(c)) delete t[k]; l.tint = Object.keys(t).length ? t : undefined; }
  return l;
}
export const BODIES = KEY3D.map((k) => ({ ...BODY_INFO[k], sheet: FILES3D[k].base }));
// The 3D accessory overlays, per body (the same grid as the sheet): loaded with the module; `otterArtReady` resolves
// once they're in (or failed), so a sheet built after it has every accessory.
const LAYERS3D = typeof Image !== 'undefined'
  ? KEY3D.map((k) => Object.fromEntries(Object.entries(FILES3D[k]).filter(([l]) => l !== 'base' && l !== 'act' && l !== 'duo').map(([l, u]) => { const i = new Image(); i.src = u; return [l, i]; })))
  : [];
export const otterArtReady = Promise.all(LAYERS3D.flatMap((L) => Object.values(L)).map((i) => (i.complete ? null : new Promise((r) => { i.onload = i.onerror = r; }))));

// ---- frames. A built sheet (buildSheet) is 12 frames a row; frame n is the n-th 32 x 32 cell, row by row.
//  0-11 the stand + walk row ([down, right, up, left] x [stand, step L, step R]: what only draws those, the dress-up
//  panel, OtterBonk, the meeting tiles, uses it), then every 3D cell from 12 on (GEO3D order): 16 directions of idle /
//  walk / run / swim / dive / climb, and float / the emotes facing down.
/** The frame of 3D animation a facing dir16 d (0 down, then clockwise on screen: 4 right, 8 up, 12 left), frame k. */
export const cell16 = (a, d, k = 0) => {
  const A = GEO3D.anim[a], dd = ((Math.round(((d & 15) * A.dirs) / 16) % A.dirs) + A.dirs) % A.dirs;
  return 12 + A.start + dd * A.frames + (k % A.frames);
};
/** All the frames of 3D animation a facing dir16 d. */
export const frames16 = (a, d) => Array.from({ length: GEO3D.anim[a].frames }, (_, k) => cell16(a, d, k));
const ROW0_3D = [0, 4, 8, 12].flatMap((d) => [cell16('idle', d, 0), cell16('walk', d, 0), cell16('walk', d, 2)]);
/** Frames by name: ANIM.runR = [frame, ...] etc. (runR/L/D/U and the diagonals, walkDR.. [stand, step L, step R],
 *  swimR/L/D/U, float, diveR/L, climb, the emotes em*), from the 8 main directions; frames16 has every direction. */
export const ANIM = (() => {
  const o = {}, D = { D: 0, DR: 2, R: 4, UR: 6, U: 8, UL: 10, L: 12, DL: 14 };
  for (const [k, d] of Object.entries(D)) {
    o['run' + k] = frames16('run', d);
    if (k.length === 2) o['walk' + k] = [cell16('idle', d, 0), cell16('walk', d, 0), cell16('walk', d, 2)];
    else o['swim' + k] = frames16('swim', d);
  }
  o.diveR = frames16('dive', 4); o.diveL = frames16('dive', 12);
  o.climb = frames16('climb', 0); // (3D: 16 directions, deck/pose.js frames16; 0 the haul-out, 1 the heave, 2 the shake)
  for (const a of Object.keys(GEO3D.anim)) if (GEO3D.anim[a].dirs === 1) o[a] = frames16(a, 0);
  return o;
})();
export const SHEET_ROWS = 1 + Math.ceil(GEO3D.cells / 12);
/** How many directions the otters are drawn in. */
export const OTTER_DIRS = 16;
// 8-way facing (deck/move.js dir8: 0 down, 1 down-right, 2 right, 3 up-right, 4 up, 5 up-left, 6 left, 7 down-left).
/** The frame of an otter standing (step 0) or on a walk step (1 step L, 2 step R) facing dir16 (0-15). */
export const facing16 = (d16, step = 0) => cell16(step ? 'walk' : 'idle', d16, step === 2 ? 2 : 0);
/** The frame of an otter standing (step 0) or on a walk step (1 step L, 2 step R) facing dir8 (0-7). */
export const facingFrame = (d8, step = 0) => facing16((d8 & 7) * 2, step);
/** The run's frames facing dir8 (0-7). */
export const runFrames = (d8) => frames16('run', (d8 & 7) * 2);
export const BODY_COUNT = BODIES.length; // the office accepts the same (src/office/look.ts BODY_COUNT: look-model.js BODY_KEYS)

// the look made from an id: look-model.js seedParts (the office makes the same one for guests), which counts the
// palettes as PART_COUNTS: they must agree with the lists above
if (FURS.length !== PART_COUNTS.fur || BELLIES.length !== PART_COUNTS.belly || SCARVES.length !== PART_COUNTS.scarf) console.error('look-model.js PART_COUNTS is out of date');
export function seedLook(seed) { return normLook(seedParts(seed)); }
export function validLook(l) {
  const ok = (v, n) => Number.isInteger(v) && v >= 0 && v < n;
  return !!l && ok(l.body, BODY_COUNT) && ok(l.fur, FURS.length) && ok(l.belly, BELLIES.length) && ok(l.scarf, SCARVES.length)
    && !!okAcc(l.acc) && (l.tint == null || !!okTint(l.tint));
}
/** The body to draw; invalid input uses the original otter. */
export const bodyOf = (l) => (Number.isInteger(l?.body) && l.body >= 0 && l.body < BODY_COUNT ? l.body : 0);
/** A look in its full shape, with stable key order for sheet caching. */
export function normLook(l) {
  const acc = accOf(l), tint = okTint(l.tint);
  const o = { body: bodyOf(l), fur: l.fur, belly: l.belly, scarf: l.scarf, acc };
  if (tint) o.tint = tint;
  return o;
}
/** A person's look, ready to draw: their saved one, or else the one made from their id. */
export const lookFor = (saved, seed) => (validLook(saved) ? normLook(saved) : seedLook(seed));

// ---- colours. Every body is drawn in a 4-shade template ramp (light, base, shade, outline) per material, so every
// fur / belly / scarf choice gets a ramp. The first entries' ramps are the template.
const T_FUR = [138, 90, 59], T_SHADE = [107, 67, 41], T_CREAM = [241, 220, 184], T_SCARF = [255, 107, 74], T_SCARF2 = [210, 74, 46];
const mix = (a, b, t) => a.map((v, i) => Math.round(v + (b[i] - v) * t));
const eq = (a, b) => a[0] === b[0] && a[1] === b[1] && a[2] === b[2];
export const TPL = {
  F: [[176, 122, 80], T_FUR, T_SHADE, [58, 32, 24]],
  C: [[255, 247, 228], T_CREAM, [214, 184, 140], [163, 128, 90]],
  S: [[255, 156, 120], T_SCARF, T_SCARF2, [150, 46, 30]],
};
// highlight warmed toward cream, outline cooled toward plum (and kept dark: it has to read on the brown deck)
export const furRamp = (f) => (eq(f.fur, T_FUR) ? TPL.F : [mix(f.fur, [255, 236, 200], 0.26), f.fur, f.shade, mix(f.shade, [34, 18, 34], 0.6)]);
export const creamRamp = (c) => (eq(c, T_CREAM) ? TPL.C : [mix(c, [255, 250, 235], 0.6), c, mix(c, [150, 100, 60], 0.3), mix(c, [80, 40, 10], 0.5)]);
// scarf shadows lean toward crimson, so a yellow scarf doesn't go olive
export const scarfRamp = (c) => (eq(c, T_SCARF) ? TPL.S : [mix(c, [255, 255, 235], 0.35), c, mix(c, [150, 40, 70], 0.28), mix(c, [90, 20, 50], 0.55)]);
const rgbKey = (c) => (c[0] << 16) | (c[1] << 8) | c[2];

// ---- custom colours (look.tint): any colour, snapped so it still makes a ramp that reads as pixel art: channels on
// a grid of 8 (no near-duplicate shades), lightness kept in a band per material (fur dark enough for its cream
// highlight, belly light, the scarf in between), and the shades generated by the ramps above from that one base.
const LIGHT_BAND = { fur: [0.2, 0.7], belly: [0.62, 0.95], scarf: [0.3, 0.72] };
const hex2rgb = (h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
export const rgb2hex = (c) => '#' + c.map((v) => v.toString(16).padStart(2, '0')).join('');
function toHsl([r, g, b]) {
  r /= 255; g /= 255; b /= 255;
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b), l = (mx + mn) / 2, d = mx - mn;
  if (!d) return [0, 0, l];
  const s = d / (1 - Math.abs(2 * l - 1)), h = mx === r ? ((g - b) / d + 6) % 6 : mx === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return [h * 60, s, l];
}
function fromHsl([h, s, l]) {
  const c = (1 - Math.abs(2 * l - 1)) * s, x = c * (1 - Math.abs(((h / 60) % 2) - 1)), m = l - c / 2;
  const [r, g, b] = h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x] : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];
  return [r, g, b].map((v) => Math.round((v + m) * 255));
}
/** A custom colour for kind ('fur' | 'belly' | 'scarf') as the otter will wear it: [r, g, b], snapped (see above). */
export function snapColour(kind, hex) {
  const [h, s, l] = toHsl(hex2rgb(hex)), [lo, hi] = LIGHT_BAND[kind];
  return fromHsl([h, Math.min(s, kind === 'belly' ? 0.7 : 0.85), Math.min(hi, Math.max(lo, l))]).map((v) => Math.min(248, Math.round(v / 8) * 8));
}
/** The colours a look wears: {fur: {fur, shade}, belly: rgb, scarf: rgb | null}, its tint over its palette picks. */
export function coloursOf(look) {
  const t = okTint(look.tint) || {}, scarf = SCARVES[look.scarf]?.c ?? null;
  const fur = t.fur ? (() => { const c = snapColour('fur', t.fur); return { fur: c, shade: mix(c, [30, 16, 24], 0.24) }; })() : FURS[look.fur];
  return { fur, belly: t.belly ? snapColour('belly', t.belly) : BELLIES[look.belly].c, scarf: scarf && t.scarf ? snapColour('scarf', t.scarf) : scarf };
}

/** One look's sheet, as a canvas (see the frames above): the body's 3D sheet with its accessory overlays, recoloured.
 *  templates: the body sheets, indexed like BODIES. A body sheet that didn't load gives a blank sheet, not a throw. */
export function buildSheet(templates, look) { return buildSheet3D(templates, look); }

/** Swap the template ramps (fur, belly, scarf) on a canvas for a look's. */
function recolour(g, W, H, fur, belly, scarf) {
  const img = g.getImageData(0, 0, W, H), d = img.data;
  const swap = new Map(), add = (from, to) => from.forEach((c0, k) => swap.set(rgbKey(c0), to[k]));
  add(TPL.F, furRamp(fur)); add(TPL.C, creamRamp(belly)); if (scarf) add(TPL.S, scarfRamp(scarf));
  for (let i = 0; i < d.length; i += 4) {
    if (!d[i + 3]) continue;
    const to = swap.get((d[i] << 16) | (d[i + 1] << 8) | d[i + 2]);
    if (to) { d[i] = to[0]; d[i + 1] = to[1]; d[i + 2] = to[2]; }
  }
  g.putImageData(img, 0, 0);
}
/** The 3D sheet for a look: the body's sheet with its accessory overlays on (the scarf, then look-model.js
 *  ACCESSORIES order), recoloured, then laid out 12 frames a row: row 0 the stand + walk row, every 3D cell from frame
 *  12 on. */
function buildSheet3D(templates, look) {
  const SW = GEO3D.cols * 32, SH = GEO3D.rows * 32, src = document.createElement('canvas'); src.width = SW; src.height = SH;
  if (!paint3D(src, templates, look, 0, 0, SW, SH)) return src; // the body sheet didn't load: a blank sheet, not a throw
  const c = document.createElement('canvas'); c.width = 32 * 12; c.height = 32 * SHEET_ROWS;
  const o = c.getContext('2d', { willReadFrequently: true });
  const put = (from, to) => o.drawImage(src, (from % GEO3D.cols) * 32, Math.floor(from / GEO3D.cols) * 32, 32, 32, (to % 12) * 32, Math.floor(to / 12) * 32, 32, 32);
  for (let i = 0; i < GEO3D.cells; i++) put(i, 12 + i);
  ROW0_3D.forEach((f, i) => put(f - 12, i));
  return c;
}
/** Paint the 3D sheet's region (sx, sy, w, h) for a look onto canvas c (w x h): body, overlays, recoloured. */
function paint3D(c, templates, look, sx, sy, w, h) {
  const body = bodyOf(look);
  return paintLayers(c, templates[body], LAYERS3D[body] || {}, look, sx, sy, w, h);
}
/** Paint sheet tpl with its overlays L (by layer name) for a look onto canvas c: the scarf, then the accessories worn. */
function paintLayers(c, tpl, L, look, sx, sy, w, h) {
  if (!tpl || !tpl.naturalWidth) return false;
  const g = c.getContext('2d', { willReadFrequently: true });
  const { fur, belly, scarf } = coloursOf(look);
  for (const l of [tpl, scarf && L.scarf, ...accOf(look).map((id) => L[id])]) if (l?.complete && l.naturalWidth) g.drawImage(l, sx, sy, w, h, 0, 0, w, h);
  recolour(g, w, h, fur, belly, scarf);
  return true;
}
// ---- the act actions (emotes.js 9-15, once the cats' own, every body's now): each body has sheets of their own for
// them (FILES3D[k].act, on the GEO3D.act grid), loaded the first time a look plays one (actReady), so the main sheets
// and OtterBonk never carry them. Built per look like the main sheet (buildActSheet), frame n = act cell n. The two-person actions
// (public/duo.js) are the same for every body: FILES3D[k].duo on the GEO3D.duo grid (duoReady, duoFrames16, buildDuoSheet).
const ACT3D = OTTER3D ? GEO3D.act ?? null : null, DUO3D = OTTER3D ? GEO3D.duo ?? null : null;
const extraArt = { act: new Map(), duo: new Map() }; // set -> body -> {layer: Image}, each loaded when first needed
/** The `set` ('act' | 'duo') sheets a look needs ({layer: Image}: the base, the scarf if it wears one, its
 *  accessories), loading from the first call; null if its body has none. */
function extraArtOf(look, set) {
  const b = bodyOf(look), files = (set === 'act' ? ACT3D : DUO3D) && FILES3D[KEY3D[b]]?.[set]; if (!files || typeof Image === 'undefined') return null;
  let L = extraArt[set].get(b); if (!L) extraArt[set].set(b, (L = {}));
  const out = {};
  for (const l of ['base', coloursOf(look).scarf && 'scarf', ...accOf(look)]) {
    if (!l || !files[l]) continue;
    if (!L[l]) { L[l] = new Image(); L[l].src = files[l]; }
    out[l] = L[l];
  }
  return out;
}
const extraReady = (look, set) => { const L = extraArtOf(look, set); return !!L && !!L.base?.naturalWidth && Object.values(L).every((i) => i.complete); };
/** The frames of animation a on grid G facing dir16 d: 16 ways, or (dirs 2: the two-person side views) d < 8 the
 *  one facing right (the partner on the right), else left. */
function gridFrames(G, a, d) {
  const A = G?.anim[a]; if (!A) return null;
  const dd = A.dirs === 2 ? ((d & 15) < 8 ? 0 : 1) : ((Math.round(((d & 15) * A.dirs) / 16) % A.dirs) + A.dirs) % A.dirs;
  return Array.from({ length: A.frames }, (_, k) => A.start + dd * A.frames + k);
}
function buildExtraSheet(look, set) {
  if (!extraReady(look, set)) return null;
  const G = set === 'act' ? ACT3D : DUO3D, L = extraArtOf(look, set), c = document.createElement('canvas'); c.width = G.cols * 32; c.height = G.rows * 32;
  paintLayers(c, L.base, L, look, 0, 0, c.width, c.height);
  return c;
}
/** Are a look's act sheets in (its body has act sheets, the ones it needs loaded or failed, the base loaded)?
 *  Starts loading them the first time it's asked. */
export const actReady = (look) => extraReady(look, 'act');
/** The act frames of animation a facing dir16 d (null: not an act animation). */
export const actFrames16 = (a, d) => gridFrames(ACT3D, a, d);
/** A look's act sheet as a canvas (ACT3D.cols x ACT3D.rows cells; frame n = cell n), or null if not ready. */
export const buildActSheet = (look) => buildExtraSheet(look, 'act');
/** Are a look's two-person sheets in? Starts loading them the first time it's asked (public/duo.js asks early: when
 *  the prompt shows, when asked, when one starts). */
export const duoReady = (look) => extraReady(look, 'duo');
/** The duo frames of two-person animation a facing dir16 d (dirs 2: d < 8 the partner on the right), or null. */
export const duoFrames16 = (a, d) => gridFrames(DUO3D, a, d);
/** A look's two-person sheet as a canvas (DUO3D.cols x DUO3D.rows cells; frame n = cell n), or null if not ready. */
export const buildDuoSheet = (look) => buildExtraSheet(look, 'duo');

/** One frame (numbered as in buildSheet's sheet) of a look, as a 32 x 32 canvas: for thumbnails, without building
 *  the whole sheet. */
export function buildFrame(templates, look, frame) {
  const c = document.createElement('canvas'); c.width = c.height = 32;
  const cell = frame < 12 ? ROW0_3D[frame] - 12 : frame - 12;
  paint3D(c, templates, look, (cell % GEO3D.cols) * 32, Math.floor(cell / GEO3D.cols) * 32, 32, 32);
  return c;
}
